import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PlugDLogo from "../common/PlugDLogo";

export default function TermsOfServicePage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
            <PlugDLogo size={28} className="rounded-lg" />
            <span className="font-bold text-lg">PlugD</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("legal_back_home", "Back to Home")}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t("legal_tos_title", "Terms of Service")}
        </h1>
        <p className="text-sm text-gray-500 mb-10">
          {t("legal_tos_last_updated", "Last updated: April 6, 2026")}
        </p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_acceptance_title", "1. Acceptance of Terms")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_acceptance_text",
                "By accessing or using PlugD (\"the Service\"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Service. These terms apply to all visitors, users, and others who access or use the Service."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_description_title", "2. Description of Service")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_description_text",
                "PlugD is an AI-powered social media management platform that enables users to create, schedule, and publish content across multiple social media platforms including Facebook, Instagram, Twitter, LinkedIn, TikTok, YouTube, Pinterest, and others. The Service includes content creation tools, scheduling features, analytics, and team collaboration capabilities."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_accounts_title", "3. User Accounts")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_accounts_text",
                "You must create an account to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate and complete information when creating your account and keep this information up to date. You must notify us immediately of any unauthorized use of your account."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_social_title", "4. Social Media Account Connections")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_social_text",
                "The Service allows you to connect your social media accounts to manage and publish content. By connecting your social media accounts, you authorize PlugD to access and interact with these accounts on your behalf, including posting content, reading analytics data, and managing comments. You may disconnect your social media accounts at any time through the Service settings. We store access tokens securely and only use them for the purposes you have authorized."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_content_title", "5. User Content")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_content_text",
                "You retain all ownership rights to content you create or upload through the Service. By using the Service to publish content to social media platforms, you grant PlugD a limited, non-exclusive license to store, process, and transmit your content as necessary to provide the Service. You are solely responsible for ensuring that your content complies with all applicable laws and the terms of service of the social media platforms you use."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_ai_title", "6. AI-Generated Content")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_ai_text",
                "The Service includes AI-powered features for content creation and optimization. AI-generated content is provided as suggestions only. You are responsible for reviewing, editing, and approving all content before it is published. PlugD does not guarantee the accuracy, appropriateness, or effectiveness of AI-generated content."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_prohibited_title", "7. Prohibited Uses")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_prohibited_text",
                "You may not use the Service to: violate any applicable laws or regulations; post spam, misleading, or fraudulent content; infringe on intellectual property rights of others; distribute malware or malicious content; attempt to gain unauthorized access to the Service or related systems; use the Service to harass, abuse, or harm others; or resell access to the Service without authorization."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_payment_title", "8. Payment and Billing")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_payment_text",
                "Certain features of the Service require a paid subscription. You agree to pay all fees associated with your selected plan. Fees are billed in advance on a monthly or annual basis. Refunds are handled in accordance with our refund policy. We reserve the right to change our pricing with reasonable notice. Your continued use of the Service after a price change constitutes acceptance of the new pricing."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_termination_title", "9. Termination")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_termination_text",
                "We may suspend or terminate your access to the Service at any time for violation of these terms or for any other reason with notice. You may terminate your account at any time through the Service settings. Upon termination, your right to use the Service will immediately cease, and we may delete your data after a reasonable retention period."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_liability_title", "10. Limitation of Liability")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_liability_text",
                "The Service is provided \"as is\" without warranties of any kind. PlugD shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you have paid us in the twelve months preceding the claim. We are not responsible for actions taken by social media platforms that may affect your content or accounts."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_changes_title", "11. Changes to Terms")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_changes_text",
                "We reserve the right to modify these terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after changes are posted constitutes acceptance of the modified terms."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_tos_contact_title", "12. Contact Us")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_tos_contact_text",
                "If you have any questions about these Terms of Service, please contact us at support@plugd.online."
              )}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} {t("land_footer_copyright", "PlugD. All rights reserved.")}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
              {t("legal_privacy_title", "Privacy Policy")}
            </Link>
            <Link to="/terms" className="text-sm text-gray-700 font-medium">
              {t("legal_tos_title", "Terms of Service")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
